import { useContext, useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SttTable, SttTopControls } from '../../../sporttia/all';
import { AppContext } from '../../../sporttia/AppContext';
import { SttFullDialog } from '../../../sporttia/dialogs/SttFullDialog';
import ConfigurationTriggerDialog from './ConfigurationTriggerDialog';
import { FilterField } from '../../../components/filter/filterTypes';
import { sporttiaColors } from '../../../theme/theme';

type Props = {
    open: boolean;
    onClose: () => void;
};

type Trigger = {
    id: number;
    created: string;
    status: string;
    request: string;
    answer: string;
    coreTriggerSC: {
        coreTrigger: {
            code: string;
        };
    };
};

type TriggersResponse = {
    rows: Trigger[];
    count?: number;
};

type Filter = {
    status: string;
};

type Params = {
    page: number;
    rows: number;
};

/**
 * Sport Center triggers dialog.
 */
export default function ConfigurationTriggersDialog({
    open,
    onClose,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [triggers, setTriggers] = useState<TriggersResponse | undefined>();
    const [params, setParams] = useState<Params>({ page: 1, rows: 20 });
    const [selectedAPITrigger, setSelectedAPITrigger] = useState<
        Trigger | undefined
    >();
    const [filter, setFilter] = useState<Filter>({ status: 'ALL' });

    /**
     * Get all api triggers.
     */
    const loadApiTriggers = (p: Params = { page: 1, rows: 20 }): void => {
        let finalFilter: Partial<Filter> = {};
        if (filter.status !== 'ALL') finalFilter = filter;

        cxt?.api('GET', `/scs/${cxt.sc?.id}/cores/triggers/executions`, {
            params: {
                ...finalFilter,
                ...p,
            },
            success: (r: TriggersResponse) => {
                setTriggers(r);
            },
        });
    };

    /**
     * Execute trigger.
     */
    const executeTrigger = (triggerId: number): void => {
        cxt?.api('POST', `/cores/triggers/executions/${triggerId}/runs`, {
            confirmation: true,
            success: () => {
                loadApiTriggers();
                cxt.showMessage('S', `Trigger con id ${triggerId} ejecutado.`);
            },
        });
    };

    useEffect(() => {
        loadApiTriggers();
    }, []);

    useEffect(() => {
        loadApiTriggers();
    }, [filter]);

    return (
        <SttFullDialog
            open={open}
            onClose={onClose}
            title={t('IntegrationOperationsTitle')}
        >
            <Paper>
                <SttTopControls
                    p={2}
                    fields={[
                        {
                            caption: t('Type'),
                            type: 'select',
                            name: 'status',
                            value: filter.status,
                            options: [
                                {
                                    label: t('All'),
                                    value: 0,
                                },
                                {
                                    label: t('ERROR'),
                                    value: 1,
                                },
                                {
                                    label: t('OK'),
                                    value: 2,
                                },
                            ],
                            mandatory: false,
                            cleanable: false,
                            onChange: ({ name, value }) => {
                                if (!name) return;
                                const statusMap: Record<number, string> = {
                                    0: 'ALL',
                                    1: 'ERROR',
                                    2: 'OK',
                                };
                                const statusValue =
                                    typeof value === 'number'
                                        ? statusMap[value]
                                        : (value as string);
                                setFilter({ ...filter, [name]: statusValue });
                            },
                        } as FilterField,
                    ]}
                    iconButtons={[
                        {
                            icon: <AutorenewIcon />,
                            onClick: () => loadApiTriggers(params),
                        },
                    ]}
                    onFilter={loadApiTriggers}
                />

                <SttTable
                    data={triggers}
                    onFetch={(p: Params) => {
                        loadApiTriggers(p);
                        setParams(p);
                    }}
                    columns={[
                        {
                            title: 'Id',
                            field: 'id',
                            value: (row: Trigger) => (
                                <Link
                                    onClick={() => setSelectedAPITrigger(row)}
                                    variant="body2"
                                >
                                    {row.id}
                                </Link>
                            ),
                        },
                        {
                            title: 'Created',
                            field: 'created',
                            type: 'dmyhm',
                        },
                        {
                            title: 'Function',
                            value: (row: Trigger) =>
                                row.coreTriggerSC.coreTrigger.code,
                        },
                        {
                            title: 'Status',
                            field: 'status',
                            value: (row: Trigger) => (
                                <b
                                    style={{
                                        color:
                                            row.status === 'OK'
                                                ? sporttiaColors.success.main
                                                : sporttiaColors.error.main,
                                    }}
                                >
                                    {row.status}
                                </b>
                            ),
                        },
                        {
                            title: '',
                            value: (row: Trigger) => (
                                <PlayArrowIcon
                                    onClick={() => executeTrigger(row.id)}
                                />
                            ),
                        },
                    ]}
                />

                <ConfigurationTriggerDialog
                    trigger={selectedAPITrigger || null}
                    onClose={() => setSelectedAPITrigger(undefined)}
                />
            </Paper>
        </SttFullDialog>
    );
}
